import { dayjsWithTimezone, DownloadCalendarButton, MMM_FORMAT } from '@healthhub/react-ui';
import { Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AppointmentsCardImg } from '@shared/assets';
import { Cluster } from '@shared/constants/constants';
import { APPOINTMENT_PATH } from '@shared/constants/paths';
import { useLayoutViewContext } from '@shared/context';
import { useGetUpcomingAppointmentQuery } from '@shared/services/appointment/getLists';
import { GroupCode } from '@shared/utils/constants';

import VcCardInformation from '@features/Appointment/shared/components/VcCardInformation';
import { INSTITUTION_CODE } from '@features/Appointment/shared/constants';
import { addCalendarAppointment } from '@features/Appointment/shared/utils/tools';

import PostLoginDashboardCard from './PostLoginDashboardCard';

const { Text } = Typography;

const AppointmentCard = () => {
  const { isMobileView, isSharedServiceView } = useLayoutViewContext();
  const { t } = useTranslation('home');
  const {
    data: appointmentData,
    isFetching: isAppointmentFetching,
    refetch: appointmentRefetch,
  } = useGetUpcomingAppointmentQuery({
    Organization: '',
  });
  const [upcomingAppointment, ...otherAppointment] = appointmentData?.Appointments ?? [];
  const upcomingAppointmentDate = upcomingAppointment?.ClinicInformation?.ApptDate
    ? dayjsWithTimezone(upcomingAppointment?.ClinicInformation?.ApptDate)
    : null;
  const upcomingAppointmentTime = upcomingAppointment?.ClinicInformation?.ApptTime;

  const clinicInformation = upcomingAppointment?.ClinicInformation;
  const isVcAppointment = !!upcomingAppointment?.IsVcSupported;

  const vcClinicInformation = useMemo(
    () => ({
      institutionName: clinicInformation?.InstitutionName ?? '',
      time: clinicInformation?.Time ?? '',
      apptDate: clinicInformation?.ApptDate ?? '',
      apptTime: clinicInformation?.ApptTime ?? '',
      service: clinicInformation?.Service ?? '',
    }),
    [clinicInformation]
  );

  const isLinkedAppointment = useMemo(() => {
    if ((otherAppointment?.length ?? 0) > 0 && upcomingAppointment.AppointmentLinkId) {
      return otherAppointment.some(
        (appointment) => appointment.AppointmentLinkId === upcomingAppointment.AppointmentLinkId
      );
    }

    return false;
  }, [otherAppointment, upcomingAppointment]);

  const onAddToCalendar = () => {
    addCalendarAppointment({
      endDate: upcomingAppointment?.End ?? '',
      isNUP:
        upcomingAppointment?.GroupCode === GroupCode.Polyclinic && upcomingAppointment?.ClusterCode === Cluster.NUHS,
      location: upcomingAppointment?.CalendarLocation ?? '',
      startDate: upcomingAppointment?.Start ?? '',
      title: upcomingAppointment?.CalendarTitle ?? '',
      layoutView: { isMobileView, isSharedServiceView },
    });
  };

  const withoutSymbolLocation = useMemo(() => {
    if (clinicInformation?.Location?.includes('|')) {
      return clinicInformation?.Location?.split('|')?.map((text) => <Text key={text}>{text}</Text>);
    }
    return clinicInformation?.Location;
  }, [clinicInformation?.Location]);

  const upcomingAppointmentContent = isVcAppointment ? (
    <div className="flex justify-between gap-4 lg:justify-normal lg:gap-12">
      <div className="gbmask flex gap-4 lg:w-[70%]">
        <VcCardInformation clinicInformation={vcClinicInformation} />
      </div>
      <div className="flex flex-col items-center gap-2.5">
        {!!upcomingAppointment?.InstitutionLogo && (
          <img
            alt={clinicInformation?.InstitutionName ?? ''}
            className="size-12px object-contain p-1"
            src={upcomingAppointment?.InstitutionLogo}
          />
        )}
        <DownloadCalendarButton onAddToCalendar={onAddToCalendar} />
      </div>
    </div>
  ) : (
    <div className="flex justify-between gap-4 lg:justify-normal lg:gap-12">
      <div className="gbmask flex gap-4 lg:w-[70%]">
        <div className="flex flex-col items-center self-start rounded-lg bg-functional-haze px-3 py-1">
          <Text className="text-xl font-semibold">{upcomingAppointmentDate?.date()}</Text>
          <Text className="text-sm font-semibold">{upcomingAppointmentDate?.format(MMM_FORMAT).toUpperCase()}</Text>
          <Text className="text-sm">{`${upcomingAppointmentDate?.year()}`}</Text>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          {!!isLinkedAppointment && (
            <Text className="\self-start rounded-xl bg-secondary-green-100 p-2 py-0.5 text-xs font-semibold text-primary-blue-grey-4">
              {t('linkedAppointment')}
            </Text>
          )}
          <div className="flex flex-col gap-1">
            <Text className="text-sm font-semibold">{clinicInformation?.InstitutionName}</Text>
            {!!upcomingAppointmentTime && (
              <Text className="text-sm font-semibold text-primary-blue-grey-3">{upcomingAppointmentTime}</Text>
            )}
          </div>
          {!!clinicInformation?.Department && <Text className="gbmask text-sm">{clinicInformation.Department}</Text>}
          {!!withoutSymbolLocation && <Text className="gbmask text-sm">{withoutSymbolLocation}</Text>}
          {!!clinicInformation?.Service && <Text className="gbmask text-sm">{clinicInformation.Service}</Text>}
          {upcomingAppointment?.Logo !== INSTITUTION_CODE.NUP && !!clinicInformation?.Provider && (
            <Text className="gbmask text-sm">{clinicInformation.Provider}</Text>
          )}
        </div>
      </div>
      <div className="flex flex-col items-center gap-2.5">
        {!!upcomingAppointment?.InstitutionLogo && (
          <img alt="" className="size-12px object-contain p-1" src={upcomingAppointment?.InstitutionLogo} />
        )}
        <DownloadCalendarButton onAddToCalendar={onAddToCalendar} />
      </div>
    </div>
  );

  return (
    <PostLoginDashboardCard
      button={{
        title: upcomingAppointment ? t('manageAppointments') : t('makeNewAppointment'),
      }}
      className={isVcAppointment ? 'min-h-72' : undefined}
      image={AppointmentsCardImg}
      isLoading={isAppointmentFetching}
      isShowRefreshButton
      onRefreshClick={appointmentRefetch}
      title={t('appointments')}
      url={APPOINTMENT_PATH}
    >
      {upcomingAppointment
        ? upcomingAppointmentContent
        : !isAppointmentFetching && <Text className="text-sm font-semibold">{t('noUpcomingAppointments')}</Text>}
    </PostLoginDashboardCard>
  );
};

export default AppointmentCard;
